import { Input } from '@tempo-io/sui-input';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '@/common/components/base-dialog/BaseDialog';
import { useTempoUsersQuery } from '@/features/settings/user-management/queries/tempoUserQueries';
import { UserRow } from './UserRow';

interface UserSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserId?: string;
  excludedUserIds?: readonly string[];
  onSave: (userId: string) => void;
  onClear?: () => void;
  title: string;
}

export const UserSelectionDialog = ({
  open,
  onOpenChange,
  selectedUserId,
  excludedUserIds,
  onSave,
  onClear,
  title,
}: UserSelectionDialogProps) => {
  const { t } = useTranslation();
  const [searchInputValue, setSearchInputValue] = useState('');

  const searchFilter = searchInputValue.trim()
    ? {
        OR: [
          { field: 'name' as const, operator: 'like' as const, value: searchInputValue },
          { field: 'email' as const, operator: 'like' as const, value: searchInputValue },
        ],
      }
    : undefined;

  const { data: allUsers = [] } = useTempoUsersQuery({
    sortBy: 'name',
    sortOrder: 'asc',
    enabled: open,
    filterBy: searchFilter,
  });

  const handleUserSelect = (userId: string) => {
    onSave(userId);
    setSearchInputValue('');
    onOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchInputValue('');
    }
    onOpenChange(isOpen);
  };

  return (
    <BaseDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={t(($) => $.adaptivePlanner.itemPanel.userField.selectUserFromList)}
      className="z-1400! h-4/6 overflow-hidden flex flex-col"
      additionalAction={
        onClear && selectedUserId
          ? {
              label: t(($) => $.adaptivePlanner.itemPanel.userField.clear),
              onClick: () => {
                onClear();
                onOpenChange(false);
              },
              'data-testid': 'user-clear-button',
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4 min-h-0 flex-1">
        <Input
          type="text"
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          placeholder={t(($) => $.adaptivePlanner.itemPanel.userField.searchUsers)}
        />

        <div className="flex flex-col gap-2 overflow-auto flex-1 min-h-0">
          {allUsers
            .filter((user) => !excludedUserIds?.includes(user.id))
            .map((user) => (
              <UserRow
                key={user.id}
                user={user}
                isChecked={selectedUserId === user.id}
                onCheckedChange={() => handleUserSelect(user.id)}
              />
            ))}
        </div>
      </div>
    </BaseDialog>
  );
};
